import * as React from 'react';
import { Alert, Badge, Col, Row } from 'reactstrap';
import { AccentDialog } from './AccentDialog';
import { AccentButton } from '../controls/AccentButton';
import { AccentSpinner } from '../controls/AccentSpinner';
import { ReorderableList } from '../controls/SortListCtrlV2';
import { AccentComboUnbound } from '../controls/unbound/AccentComboUnbound';
import { AccentTextUnbound } from '../controls/unbound/AccentTextUnBound';
import { AccentNumberUnbound } from '../controls/unbound/AccentNumberUnbound';
import { AccentCheckUnbound } from '../controls/unbound/AccentCheckUnbound';
import { AccentUploadButton } from '../controls/AccentUpload';
import { download, queryNoTracking, update, updateAsFormData } from '../services/DataService';
import { accentUtils, format, showOK } from '../services/HelperService';
import AccentIcons from '../icons/Icons';

const query_CostcoPaymentMethod_SelectList = queryNoTracking("CostcoPaymentMethod_SelectList");
const query_CostcoLead_ContactMembershipNumber = queryNoTracking("CostcoLead_ContactMembershipNumber");

const costcoCompletedTag = "CostcoCompleted";
const sketchTag = "Sketch";

// Only these types survive the server-side mergeable filter, so restrict the upload to them.
const mergeableExtensions = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".bmp"];

const getFileNameFromUri = (uri) => {
    if (accentUtils.isNullOrEmpty(uri)) return null;
    return uri.replace(/\\/g, '/').split('/').pop();
};

export const CostcoCompleteLeadDlg = ({ lead, onClose }) => {
    const [loading, setLoading] = React.useState(true);
    const [paymentMethods, setPaymentMethods] = React.useState([]);
    const [documents, setDocuments] = React.useState([]);
    const [includedIDs, setIncludedIDs] = React.useState([]);
    const [itemsPreview, setItemsPreview] = React.useState(null);
    const [soldAmount, setSoldAmount] = React.useState(lead.SoldAmountSent);
    const [uploadKey, setUploadKey] = React.useState(1);
    const [uploadFiles, setUploadFiles] = React.useState([]);
    const [attaching, setAttaching] = React.useState(false);
    const uploadCtrl = React.useRef(null);
    const orderedDocsRef = React.useRef([]);
    const [membershipNumber, setMembershipNumber] = React.useState(null);
    const values = React.useRef({
        CostcoPaymentMethodID: lead.CostcoPaymentMethodID,
        MembershipNumber: null,
        CostcoShopCardAmount: lead.CostcoShopCardAmount,
        SoldAmount: lead.SoldAmountSent
    });

    React.useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            const [methods, docsResult, itemsResult, contactMembership] = await Promise.all([
                query_CostcoPaymentMethod_SelectList.getAll({ includeID: lead.CostcoPaymentMethodID }),
                update("CostcoIntegration_GetCompleteLeadDocuments", { CostcoLeadID: lead.ID }),
                update("CostcoIntegration_GetCompleteLeadItems", { CostcoLeadID: lead.ID }),
                query_CostcoLead_ContactMembershipNumber.getFirstOrDefault({ id: lead.ID })
            ]);

            if (!isMounted) return;

            const docs = docsResult?.Success ? (docsResult.Documents ?? []) : [];
            orderedDocsRef.current = docs;

            const contactMembershipNumber = contactMembership?.MembershipNumber ?? null;
            values.current.MembershipNumber = contactMembershipNumber;
            setMembershipNumber(contactMembershipNumber);

            setPaymentMethods(methods ?? []);
            setDocuments(docs);
            setItemsPreview(itemsResult?.Success ? (itemsResult.Items ?? null) : null);
            setLoading(false);
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [lead]);

    const setValue = (name, value) => {
        values.current[name] = value;
    };

    const toggleInclude = (id, checked) => {
        setIncludedIDs(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
    };

    // Uploads the selected files against the lead's job, then refreshes the document list.
    // New documents are appended (preserving the user's current order) and auto-selected.
    const doAttach = async (e) => {
        setAttaching(true);

        try {
            const uploadResult = await uploadCtrl.current.getFilesForUpload();

            const res = await updateAsFormData("CostcoIntegration_AttachLeadDocuments", {
                CostcoLeadID: lead.ID,
                Files: uploadResult.files
            });

            if (!res?.Success) {
                showOK("Attach Documents", res?.Message || "Unable to attach the documents.", ["application_strings.application.buttons.ok"], false, null, true);
                return;
            }

            const newIDs = res.DocumentIDs ?? [];

            const docsResult = await update("CostcoIntegration_GetCompleteLeadDocuments", { CostcoLeadID: lead.ID }, true);
            const refreshed = docsResult?.Success ? (docsResult.Documents ?? []) : [];

            const merged = [
                ...orderedDocsRef.current
                    .map(d => refreshed.find(r => r.ID === d.ID))
                    .filter(d => !accentUtils.isNull(d)),
                ...refreshed.filter(r => !orderedDocsRef.current.some(d => d.ID === r.ID))
            ];

            orderedDocsRef.current = merged;
            setDocuments(merged);
            setIncludedIDs(prev => [...prev, ...newIDs.filter(id => merged.some(d => d.ID === id) && !prev.includes(id))]);

            // Remount the upload control so the attached files are cleared.
            setUploadFiles([]);
            setUploadKey(k => k + 1);
        } finally {
            setAttaching(false);
            e.promise();
        }
    };

    const downloadDocument = (item) => {
        const route = item.Tag === sketchTag ? '/Download/DocumentSketchUri/?uri=' : '/Download/DocumentUri/?uri=';
        download(route + encodeURIComponent(item.Uri));
    };

    // Highest version per tag, so the current/live version of generated documents (e.g. quotes) can be flagged.
    const latestVersionByTag = React.useMemo(() => {
        const result = {};
        documents.forEach(d => {
            if (accentUtils.isNullOrEmpty(d.Tag) || accentUtils.isNull(d.Version)) return;
            if (accentUtils.isNull(result[d.Tag]) || d.Version > result[d.Tag]) {
                result[d.Tag] = d.Version;
            }
        });
        return result;
    }, [documents]);

    const renderDocument = ({ item }) => {
        const isPreviousMerge = item.Tag === costcoCompletedTag;
        const versionSuffix = accentUtils.isNull(item.Version) ? "" : ` v${item.Version}`;
        const isLatestOfTag = !isPreviousMerge
            && !accentUtils.isNull(item.Version)
            && latestVersionByTag[item.Tag] === item.Version;
        const fileName = getFileNameFromUri(item.Uri);
        const dateText = accentUtils.isNull(item.Date) ? null : format.formatDate(new Date(item.Date));

        return (
            <div className="d-flex align-items-center" style={{ padding: "4px 8px", gap: "8px", width: "100%", backgroundColor: isPreviousMerge ? "#fff8e1" : undefined }}>
                <div><AccentIcons.Download onClick={() => downloadDocument(item)} top={0} /></div>
                <AccentCheckUnbound defaultValue={includedIDs.includes(item.ID)} onChange={e => toggleInclude(item.ID, e.checked)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.Description}>
                        {item.Description}
                        {isPreviousMerge
                            ? <Badge color="warning" className="ms-2">{`Previous merge${versionSuffix}`}</Badge>
                            : !accentUtils.isNullOrEmpty(item.Tag) && <Badge color="secondary" className="ms-2">{`${item.Tag}${versionSuffix}`}</Badge>}
                        {isLatestOfTag && <Badge color="success" className="ms-1">Latest</Badge>}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={fileName ?? undefined}>
                        {[fileName, dateText].filter(x => !accentUtils.isNullOrEmpty(x)).join(" — ")}
                    </div>
                </div>
            </div>
        );
    };

    const getValidationError = () => {
        const v = values.current;

        if (accentUtils.isNull(v.CostcoPaymentMethodID)) return "A payment method is required to complete the lead.";
        if (accentUtils.isNull(v.SoldAmount) || v.SoldAmount <= 0) return "A sold amount greater than zero is required to complete the lead.";

        return null;
    };

    const doComplete = (e) => {
        const validationError = getValidationError();
        if (validationError) {
            showOK("Complete Costco Lead", validationError, ["application_strings.application.buttons.ok"], false, null, true);
            e.promise();
            return;
        }

        const documentIDs = orderedDocsRef.current
            .filter(d => includedIDs.includes(d.ID))
            .map(d => d.ID);

        onClose({ payload: { ...values.current, DocumentIDs: documentIDs } });
        e.promise();
    };

    const selectedCount = includedIDs.length;

    // Completing requires an itemized list grouped by Costco product code; block when it can't be produced.
    const itemsBlocked = !loading && (
        accentUtils.isNull(itemsPreview)
        || !itemsPreview.HasJobLines
        || (itemsPreview.UnmappedProducts?.length ?? 0) > 0);

    return (
        <AccentDialog
            headerContent="Complete Costco Lead"
            onClose={onClose}
            maxWidth={900}
            actionsContent={[
                <AccentButton key="complete" disabled={loading || itemsBlocked} onClick={doComplete}>Complete Lead</AccentButton>
            ]}
        >
            {loading && <AccentSpinner />}
            {!loading && (
                <>
                    <Row>
                        <Col md={12} className="mb-2">
                            <label>Review the values below before they are sent to Centah. Changes are saved back to the Costco lead.</label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <AccentComboUnbound
                                label="Payment Method"
                                required
                                data={paymentMethods}
                                valueField="ID"
                                displayField="DisplayName"
                                defaultValue={lead.CostcoPaymentMethodID}
                                onChange={v => setValue("CostcoPaymentMethodID", v)} />
                        </Col>
                        <Col md={6}>
                            <AccentTextUnbound label="Membership Number" defaultValue={membershipNumber} onChange={v => setValue("MembershipNumber", v)} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <AccentNumberUnbound label="Costco Shop Card" defaultValue={lead.CostcoShopCardAmount} onChange={v => setValue("CostcoShopCardAmount", v)} />
                        </Col>
                        <Col md={6}>
                            <AccentNumberUnbound label="Sold Amount (ex tax)" required defaultValue={lead.SoldAmountSent} onChange={v => { setValue("SoldAmount", v); setSoldAmount(v); }} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <h5 className="mt-3">Items</h5>
                            <label>The itemized amounts sent to Centah, grouped by Costco product code.</label>
                        </Col>
                    </Row>
                    {accentUtils.isNull(itemsPreview) && (
                        <Row>
                            <Col md={12}>
                                <Alert color="warning">Unable to load the itemized amounts for this lead.</Alert>
                            </Col>
                        </Row>
                    )}
                    {!accentUtils.isNull(itemsPreview) && !itemsPreview.HasJobLines && (
                        <Row>
                            <Col md={12}>
                                <Alert color="danger">
                                    No confirmed job lines were found to itemize for this Costco lead. The lead cannot be completed until the sold job's lines are available.
                                </Alert>
                            </Col>
                        </Row>
                    )}
                    {!accentUtils.isNull(itemsPreview) && (itemsPreview.UnmappedProducts?.length ?? 0) > 0 && (
                        <Row>
                            <Col md={12}>
                                <Alert color="danger">
                                    <div style={{ fontWeight: "bold" }}>The following products have no Costco product code assigned:</div>
                                    <ul className="mb-1 mt-1">
                                        {itemsPreview.UnmappedProducts.map((p, i) => <li key={i}>{p}</li>)}
                                    </ul>
                                    Assign codes under Integration Settings &gt; Costco &gt; Product Codes.
                                </Alert>
                            </Col>
                        </Row>
                    )}
                    {!accentUtils.isNull(itemsPreview) && (itemsPreview.Groups?.length ?? 0) > 0 && (
                        <Row>
                            <Col md={12}>
                                <table className="table table-sm align-middle" style={{ maxWidth: "600px" }}>
                                    <thead>
                                        <tr>
                                            <th>Costco Code</th>
                                            <th className="text-end" style={{ width: "160px" }}>Price (ex tax)</th>
                                            <th className="text-end" style={{ width: "160px" }}>Provincial Tax</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itemsPreview.Groups.map(g => (
                                            <tr key={g.Code}>
                                                <td>{g.Code}</td>
                                                <td className="text-end">{format.formatCurrency(g.Price)}</td>
                                                <td className="text-end">{format.formatCurrency(g.Tax)}</td>
                                            </tr>
                                        ))}
                                        <tr style={{ fontWeight: "bold" }}>
                                            <td>Total</td>
                                            <td className="text-end">{format.formatCurrency(itemsPreview.LineTotal)}</td>
                                            <td className="text-end">{format.formatCurrency(itemsPreview.Groups.reduce((sum, g) => sum + g.Tax, 0))}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                {!accentUtils.isNull(soldAmount) && soldAmount > 0 && soldAmount !== itemsPreview.LineTotal && (
                                    <Alert color="info" className="mb-0">
                                        {`The sold amount (${format.formatCurrency(soldAmount)}) differs from the line total (${format.formatCurrency(itemsPreview.LineTotal)}). Item prices will be adjusted proportionally so they sum to the sold amount.`}
                                    </Alert>
                                )}
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col md={12}>
                            <h5 className="mt-3">Documents</h5>
                            <label>
                                Select the documents to merge into a single CostcoCompleted.pdf that is saved against the job and uploaded with the completion.
                                Drag or use the arrows to set the page order.
                                {selectedCount > 0 ? ` ${selectedCount} selected.` : " None selected (optional)."}
                            </label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            {documents.length === 0 && <label>No documents are available to merge for this job.</label>}
                            {documents.length > 0 && (
                                <ReorderableList
                                    idField="ID"
                                    items={documents}
                                    itemRenderer={renderDocument}
                                    onOrderChange={items => { orderedDocsRef.current = items; }} />
                            )}
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md={12} className="d-flex">
                            <div className="flex-fill">
                                <AccentUploadButton
                                    key={uploadKey}
                                    ref={uploadCtrl}
                                    multiple
                                    style={{ height: "56px" }}
                                    allowedExtensions={mergeableExtensions}
                                    placeholder="Add a PDF or image to this job..."
                                    onChange={files => setUploadFiles(files ?? [])} />
                            </div>
                            <div className="d-flex align-items-center ps-2">
                                <AccentButton
                                    size="sm"
                                    style={{ height: "56px" }}
                                    disabled={attaching || uploadFiles.length === 0 || uploadFiles.some(f => f.error)}
                                    onClick={doAttach}>
                                    Attach
                                </AccentButton>
                            </div>
                        </Col>
                    </Row>
                </>
            )}
        </AccentDialog>
    );
};
